import { useEffect, useRef, useState } from 'react'
import { useTheme } from '../context/ThemeContext'
import Icon from './Icon'
import TrustedDevicesModal from './TrustedDevicesModal'
import { fetchMyMfaStatus } from '../api/security'
import styles from './UserMenu.module.css'

// JWT claim key -> display label. Only rights that are actually on (1) render —
// see the popover below. Keys match the token's `rights` object (gaussiq-identity's
// user_rights columns), not the Admin tab's camelCase SECURITY_RIGHTS keys.
const RIGHT_LABELS = [
  { key: 'superadmin_right', label: 'Super Admin' },
  { key: 'admin_right', label: 'Admin' },
  { key: 'org_skill_management', label: 'Org Skill Management' },
  { key: 'team_skill_management', label: 'Team Skill Management' },
]

// Same mapping AdminUserRow.jsx uses for the Users table's 2FA column — kept
// as its own small copy here rather than a shared module, since it's just
// this one 2-entry lookup and the two components don't otherwise share code.
const MFA_DISPLAY = {
  totp: { icon: 'lock', label: 'Authenticator app' },
  email: { icon: 'mail', label: 'Email code' },
}

/**
 * UserMenu — the navbar avatar plus its click popover (no hover tooltip).
 *
 * Props:
 *   user     {{ name, email, initial }} — display profile (may be null)
 *   rights   {object}                    — JWT `rights` claim (may be null) —
 *     the popover lists which of RIGHT_LABELS are on, one per line, so a user
 *     can see their own permissions without a trip to the Admin tab (where
 *     their own row is hidden entirely — see AdminUsersTab). superadmin_right
 *     collapses this to just "Super Admin" — it overrides the others anyway.
 *   onLogout {function}                  — called when the user signs out
 */
export default function UserMenu({ user, rights, onLogout }) {
  const [open, setOpen] = useState(false)
  const [devicesOpen, setDevicesOpen] = useState(false)
  const [mfaStatus, setMfaStatus] = useState({ method: null, enrolled: false })
  const rootRef = useRef(null)
  const { theme, toggleTheme } = useTheme()

  const initial = user?.initial || '?'
  const name    = user?.name || ''
  const email   = user?.email || ''
  // superadmin_right overrides every other right (see AgentSkillsModal/
  // skills.jsx/Sidebar) regardless of what those columns actually hold, so
  // for display it's the only one shown when set — listing the others too
  // would just be noise on top of a right that already implies them all.
  const isSuperAdmin = Boolean(rights?.superadmin_right)
  const activeRights = isSuperAdmin
    ? RIGHT_LABELS.filter(r => r.key === 'superadmin_right')
    : RIGHT_LABELS.filter(r => Boolean(rights?.[r.key]))

  // Not in the JWT (rights is), so it needs its own fetch — once on mount,
  // same lightweight one-shot pattern AdminUsersTab uses for its own list.
  // Defaults to { method: null, enrolled: false } so the badge below simply
  // doesn't render until this resolves, rather than flashing on then off.
  useEffect(() => {
    let cancelled = false
    fetchMyMfaStatus()
      .then(status => { if (!cancelled) setMfaStatus(status) })
      .catch(() => { /* badge just stays hidden — nothing actionable to show here */ })
    return () => { cancelled = true }
  }, [])

  const mfaDisplay = mfaStatus.enrolled ? MFA_DISPLAY[mfaStatus.method] : null

  // Close the popover on outside click or Escape so it behaves like a menu.
  useEffect(() => {
    if (!open) return
    function onPointerDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    function onKeyDown(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div className={styles.root} ref={rootRef}>
      {/* Avatar — shows the initial; opens the popover on click */}
      <button
        type="button"
        className={styles.avatar}
        onClick={() => setOpen(prev => !prev)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Account menu"
      >
        {initial}
      </button>

      {/* Click popover — name/email, permissions (one per line), logout */}
      {open && (
        <div className={styles.popover} role="dialog" aria-label="Account">
          <div className={styles.topLeftActions}>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
            </button>

            {mfaDisplay && (
              <button
                type="button"
                className={styles.mfaBadgeBtn}
                onClick={() => { setDevicesOpen(true); setOpen(false) }}
                aria-label={`Two-factor authentication is on (${mfaDisplay.label}) — manage trusted devices`}
                title={`Two-factor: ${mfaDisplay.label} — manage trusted devices`}
              >
                <Icon name={mfaDisplay.icon} />
              </button>
            )}
          </div>

          <button
            type="button"
            className={styles.closeBtn}
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            <Icon name="close" />
          </button>

          {/* Larger circle — future home for a profile photo */}
          <div className={styles.bigAvatar}>{initial}</div>

          {name && <p className={styles.popoverName}>{name}</p>}
          <p className={styles.popoverEmail}>{email}</p>

          {activeRights.length > 0 && (
            <div className={styles.rightsList}>
              {activeRights.map(r => (
                <span key={r.key} className={styles.rightItem}>{r.label}</span>
              ))}
            </div>
          )}

          <button type="button" className={styles.logoutBtn} onClick={onLogout}>
            <span className={styles.logoutIcon}><Icon name="logout" /></span>
            <span>Logout</span>
          </button>
        </div>
      )}

      {devicesOpen && (
        <TrustedDevicesModal
          mfaMethod={mfaStatus.method}
          onClose={() => setDevicesOpen(false)}
        />
      )}
    </div>
  )
}
